import React, { useEffect, useRef, useState } from 'react';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import SearchIcon from '@mui/icons-material/Search';

interface Props {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  label?: string;
  /** Shown on the collapsed control when nothing is selected. */
  placeholder?: string;
  /** Pluralized noun for the search box placeholder ("Search lenses"). */
  pluralLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  /**
   * Render the menu in normal document flow instead of as an absolute overlay.
   * Use inside scroll containers (e.g. the Create Database modal body) that
   * would otherwise clip a floating panel.
   */
  inline?: boolean;
}

/**
 * Searchable multi-select for BITE lenses — mirrors the Export Screening tool's
 * lens dropdown (see the `entity-screening-parameter-*` markup in
 * `components/Screening/searchComponents/Search.tsx`) and reuses its global CSS.
 */
const LensMultiSelect: React.FC<Props> = ({
  options,
  selected,
  onChange,
  label,
  placeholder = 'All lenses',
  pluralLabel = 'lenses',
  loading = false,
  disabled = false,
  inline = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  // Collapse the menu when the user clicks outside of it.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const summary = !selected.length
    ? placeholder
    : selected.length === 1
    ? selected[0]
    : `${selected.length} selected`;

  // Dedupe options, fold in any selected value no longer present in the option
  // list (so it can always be unchecked), filter by search, selected-first.
  const buildOptions = (): string[] => {
    const seen = new Set<string>();
    const deduped: string[] = [];
    options.forEach((o) => {
      const v = String(o).trim();
      if (v && !seen.has(v)) {
        seen.add(v);
        deduped.push(v);
      }
    });
    selected.forEach((v) => {
      const t = String(v).trim();
      if (t && !seen.has(t)) {
        seen.add(t);
        deduped.push(t);
      }
    });
    const query = search.toLowerCase();
    const filtered = query
      ? deduped.filter((o) => o.toLowerCase().includes(query))
      : deduped;
    const selectedSet = new Set(selected);
    return [
      ...filtered.filter((o) => selectedSet.has(o)),
      ...filtered.filter((o) => !selectedSet.has(o)),
    ];
  };

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };

  const opts = buildOptions();

  return (
    <div
      className={`entity-screening-parameter-field${open ? ' active' : ''}`}
      ref={ref}
    >
      {label && (
        <label>
          <ShieldOutlinedIcon style={{ fontSize: 18 }} />
          {label}
        </label>
      )}
      <button
        type='button'
        className={`entity-screening-parameter-select${open ? ' active' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span>{summary}</span>
        <ExpandMoreOutlinedIcon style={{ fontSize: 22 }} />
      </button>
      {open && (
        <div
          className={`entity-screening-parameter-menu${
            inline ? ' eh-inline-menu' : ''
          }`}
        >
          <div className='entity-screening-parameter-search'>
            <SearchIcon style={{ fontSize: 18 }} />
            <input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${pluralLabel}`}
            />
          </div>
          <div className='entity-screening-parameter-options'>
            {loading ? (
              <span className='entity-screening-parameter-empty'>Loading…</span>
            ) : opts.length ? (
              opts.map((option) => {
                const isSelected = selected.includes(option);
                return (
                  <button
                    type='button'
                    key={option}
                    className='entity-screening-parameter-option'
                    onClick={() => toggle(option)}
                  >
                    <span
                      className={`entity-screening-parameter-check${
                        isSelected ? ' selected' : ''
                      }`}
                    />
                    <span>{option}</span>
                  </button>
                );
              })
            ) : (
              <span className='entity-screening-parameter-empty'>
                No lenses available
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LensMultiSelect;
